import { Link } from "react-router-dom";
// @ts-ignore - JS module, no types
import UserService from "../../services/UserService";
import { mapUrlToProductId } from "../../utils/common/mapUrlToProduct";
import {
  productRoleName,
  rolesAtOrAbove,
  TIER_LABELS,
  type ProductRoleTier,
} from "../../utils/common/productRoles";

/**
 * Role gating for Risk-Based Pricing Engine (product 24).
 *
 * Roles are Keycloak realm roles named "24-<tier>", checked client-side through
 * UserService.hasRole. This is presentation only -- it decides what to render, never
 * what is allowed. The real gate is the backend's require_tier dependency
 * (risk-pricing-engine/app/utils/roles.py), which reads the X-User-Roles header the
 * platform-api gateway injects from the already-verified JWT. Hiding a button here and
 * gating the endpoint there are two halves of the same rule; neither is optional.
 */

export const RPE_PRODUCT_ID = mapUrlToProductId("risk-pricing-engine") as number;

/**
 * True when the signed-in user holds `tier` for this product, or anything outranking
 * it. `rolesAtOrAbove` handles the ordering, in which CONTRIBUTOR OUTRANKS REVIEWER --
 * which is what makes "reviewer proposes, contributor approves" a single threshold test
 * rather than two separate role lists.
 */
export function hasTier(tier: ProductRoleTier): boolean {
  return UserService.hasRole(rolesAtOrAbove(RPE_PRODUCT_ID, tier));
}

/**
 * Exact-match admin test, deliberately not `hasTier("admin")`.
 *
 * They return the same thing, but this one reads as "is the product admin" rather than
 * "meets the admin threshold", and it is the form underwriting-engine uses for the same
 * admin-only affordances. Note an admin-only account is denied the product surface by
 * design (platform-api's has_any_product_role excludes the admin tier), so this mostly
 * guards Manage Access.
 */
export function isProductAdmin(): boolean {
  return UserService.hasRole([productRoleName(RPE_PRODUCT_ID, "admin")]);
}

export function AccessDenied({
  requires,
  message,
}: {
  /** Tier the page needs; used to phrase the explanation. */
  requires?: ProductRoleTier;
  /** Overrides the generated sentence entirely. */
  message?: string;
}) {
  const explanation =
    message ??
    (requires
      ? `This page is restricted to ${TIER_LABELS[requires]}s and above.`
      : "You do not have access to this page.");

  return (
    <div className="flex min-h-[60vh] items-center justify-center bg-background">
      <div className="space-y-3 px-6 text-center">
        <div className="text-[11px] font-medium uppercase tracking-[0.16em] text-amber">
          Restricted
        </div>
        <h2 className="text-lg font-semibold text-amber">Access Denied</h2>
        <p className="text-sm text-muted-foreground">{explanation}</p>
        <Link
          to="/risk-pricing-engine/state-of-play"
          className="inline-block text-sm text-amber hover:underline"
        >
          ← Back to State of Play
        </Link>
      </div>
    </div>
  );
}

/**
 * Route guard. Hiding a page from the nav is not enough on its own -- a bookmark, a
 * shared link or the browser's history would still walk straight into it.
 */
export function RequireTier({
  tier,
  children,
}: {
  tier: ProductRoleTier;
  children: React.ReactNode;
}) {
  if (!hasTier(tier)) return <AccessDenied requires={tier} />;
  return <>{children}</>;
}
